/**
 * Theme Context for the Student Immunization Portal
 * Provides theme configuration and white-labeling support to all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { ThemeConfig } from '../types';

/**
 * Default theme configuration
 */
const defaultTheme: ThemeConfig = {
  brandName: 'Student Immunization Portal',
  logoPath: '',
  colors: {
    primary: '#2563EB',
    secondary: '#1E40AF',
    accent: '#C9E4CA',
    background: '#F5F7F9',
    text: '#1A1A1A',
    textSecondary: '#6B7280',
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: {
      base: '16px',
      large: '18px',
      xlarge: '24px',
    },
  },
  accessibility: {
    minimumContrast: 4.5,
    validateContrast: true,
  },
  features: {
    showLiveHelp: true,
    allowWhatsApp: true,
    allowIDVerification: true,
  },
  contactInfo: {
    supportPhone: '1-800-555-0199',
    supportEmail: 'help@lucena.health',
  },
  kiosk: {
    inactivityTimeout: 120,
    showPrintButton: true,
    restrictedNavigation: true,
  },
};

/**
 * Theme state interface
 */
export interface ThemeState {
  theme: ThemeConfig;
  isLoading: boolean;
  error: Error | null;
  highContrastMode: boolean;
  largeTextMode: boolean;
}

/**
 * Theme context interface
 */
export interface ThemeContextValue extends ThemeState {
  // Theme settings
  reloadTheme: () => Promise<void>;
  updateColors: (colors: Partial<ThemeConfig['colors']>) => void;
  
  // Accessibility
  toggleHighContrast: () => void;
  toggleLargeText: () => void;
  
  // Feature flags
  isFeatureEnabled: (feature: keyof ThemeConfig['features']) => boolean;
  
  // Helpers
  getColor: (colorKey: keyof ThemeConfig['colors']) => string;
  getFontSize: (sizeKey: keyof ThemeConfig['typography']['fontSize']) => string;
}

/**
 * Default context value
 */
const defaultContextValue: ThemeContextValue = {
  theme: defaultTheme,
  isLoading: true,
  error: null,
  highContrastMode: false,
  largeTextMode: false,
  reloadTheme: async () => {},
  updateColors: () => {},
  toggleHighContrast: () => {},
  toggleLargeText: () => {},
  isFeatureEnabled: () => true,
  getColor: () => '',
  getFontSize: () => '16px',
};

/**
 * Theme context
 */
const ThemeContext = createContext<ThemeContextValue>(defaultContextValue);

/**
 * Theme provider props
 */
interface ThemeProviderProps {
  children: ReactNode;
  configPath?: string;
}

/**
 * Theme provider component
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  configPath = '/config/theme.json' 
}) => {
  const [state, setState] = useState<ThemeState>({
    theme: defaultTheme,
    isLoading: true,
    error: null,
    highContrastMode: localStorage.getItem('highContrastMode') === 'true',
    largeTextMode: localStorage.getItem('largeTextMode') === 'true',
  });

  /**
   * Apply CSS variables from theme config
   */
  const applyThemeVariables = useCallback((theme: ThemeConfig, highContrast: boolean, largeText: boolean) => {
    const root = document.documentElement;

    // Apply color variables
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Apply high contrast overrides if enabled
    if (highContrast) {
      root.style.setProperty('--color-text', '#000000');
      root.style.setProperty('--color-background', '#FFFFFF');
      root.style.setProperty('--color-primary', '#0000EE');
    }

    // Apply typography variables
    root.style.setProperty('--font-family', theme.typography.fontFamily);
    
    Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
      let fontSize = value;
      
      // Apply large text mode
      if (largeText) {
        const numValue = parseInt(value);
        fontSize = `${Math.round(numValue * 1.25)}px`;
      }
      
      root.style.setProperty(`--font-size-${key}`, fontSize);
    });

    // Set body font
    document.body.style.fontFamily = theme.typography.fontFamily;
    
    // Apply accessibility classes
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    if (largeText) {
      document.body.classList.add('large-text');
    } else {
      document.body.classList.remove('large-text');
    }
  }, []);

  /**
   * Load theme from config file
   */
  const loadTheme = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(configPath);
      
      if (!response.ok) {
        throw new Error('Failed to load theme configuration');
      }
      
      const config: ThemeConfig = await response.json();
      
      // Merge with defaults to ensure all properties exist
      const mergedTheme: ThemeConfig = {
        ...defaultTheme,
        ...config,
        colors: { ...defaultTheme.colors, ...config.colors },
        typography: {
          ...defaultTheme.typography,
          ...config.typography,
          fontSize: { ...defaultTheme.typography.fontSize, ...config.typography?.fontSize },
        },
        accessibility: { ...defaultTheme.accessibility, ...config.accessibility },
        features: { ...defaultTheme.features, ...config.features },
        contactInfo: { ...defaultTheme.contactInfo, ...config.contactInfo },
        kiosk: { ...defaultTheme.kiosk, ...config.kiosk },
      };

      // Validate contrast if enabled
      if (mergedTheme.accessibility?.validateContrast) {
        validateContrastRatios(mergedTheme);
      }

      // Apply CSS variables
      applyThemeVariables(mergedTheme, state.highContrastMode, state.largeTextMode);

      setState(prev => ({
        ...prev,
        theme: mergedTheme,
        isLoading: false,
      }));
    } catch (err) {
      console.error('Theme loading error:', err);
      
      // Use default theme on error
      applyThemeVariables(defaultTheme, state.highContrastMode, state.largeTextMode);
      
      setState(prev => ({
        ...prev,
        theme: defaultTheme,
        isLoading: false,
        error: err as Error,
      }));
    }
  }, [configPath, applyThemeVariables, state.highContrastMode, state.largeTextMode]);

  /**
   * Reload theme
   */
  const reloadTheme = useCallback(async () => {
    await loadTheme();
  }, [loadTheme]);

  /**
   * Update colors dynamically
   */
  const updateColors = useCallback((colors: Partial<ThemeConfig['colors']>) => {
    setState(prev => {
      const newTheme = {
        ...prev.theme,
        colors: { ...prev.theme.colors, ...colors },
      };
      
      applyThemeVariables(newTheme, prev.highContrastMode, prev.largeTextMode);
      
      return { ...prev, theme: newTheme };
    });
  }, [applyThemeVariables]);

  /**
   * Toggle high contrast mode
   */
  const toggleHighContrast = useCallback(() => {
    setState(prev => {
      const newHighContrast = !prev.highContrastMode;
      localStorage.setItem('highContrastMode', String(newHighContrast));
      applyThemeVariables(prev.theme, newHighContrast, prev.largeTextMode);
      return { ...prev, highContrastMode: newHighContrast };
    });
  }, [applyThemeVariables]);

  /**
   * Toggle large text mode
   */
  const toggleLargeText = useCallback(() => {
    setState(prev => {
      const newLargeText = !prev.largeTextMode;
      localStorage.setItem('largeTextMode', String(newLargeText));
      applyThemeVariables(prev.theme, prev.highContrastMode, newLargeText);
      return { ...prev, largeTextMode: newLargeText };
    });
  }, [applyThemeVariables]);

  /**
   * Check if feature is enabled
   */
  const isFeatureEnabled = useCallback((feature: keyof ThemeConfig['features']): boolean => {
    return state.theme.features?.[feature] ?? true;
  }, [state.theme.features]);

  /**
   * Get color value
   */
  const getColor = useCallback((colorKey: keyof ThemeConfig['colors']): string => {
    return state.theme.colors[colorKey] || defaultTheme.colors[colorKey];
  }, [state.theme.colors]);

  /**
   * Get font size value
   */
  const getFontSize = useCallback((sizeKey: keyof ThemeConfig['typography']['fontSize']): string => {
    return state.theme.typography.fontSize[sizeKey] || defaultTheme.typography.fontSize[sizeKey];
  }, [state.theme.typography.fontSize]);

  // Load theme on mount
  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  /**
   * Context value
   */
  const value = useMemo<ThemeContextValue>(() => ({
    ...state,
    reloadTheme,
    updateColors,
    toggleHighContrast,
    toggleLargeText,
    isFeatureEnabled,
    getColor,
    getFontSize,
  }), [
    state,
    reloadTheme,
    updateColors,
    toggleHighContrast,
    toggleLargeText,
    isFeatureEnabled,
    getColor,
    getFontSize,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 */
export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  
  return context;
};

/**
 * Validate contrast ratios between colors
 */
const validateContrastRatios = (theme: ThemeConfig): void => {
  const { colors, accessibility } = theme;
  const minContrast = accessibility?.minimumContrast || 4.5;

  // Calculate relative luminance
  const getLuminance = (hexColor: string): number => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const [R, G, B] = [r, g, b].map((c) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    );

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  };

  // Calculate contrast ratio
  const getContrastRatio = (color1: string, color2: string): number => {
    const l1 = getLuminance(color1);
    const l2 = getLuminance(color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  };

  // Check text on background
  const textOnBackground = getContrastRatio(colors.text, colors.background);
  if (textOnBackground < minContrast) {
    console.warn(
      `Low contrast ratio (${textOnBackground.toFixed(2)}:1) between text and background. ` +
      `Minimum required: ${minContrast}:1`
    );
  }

  // Check primary on background
  const primaryOnBackground = getContrastRatio(colors.primary, colors.background);
  if (primaryOnBackground < minContrast) {
    console.warn(
      `Low contrast ratio (${primaryOnBackground.toFixed(2)}:1) between primary and background. ` +
      `Minimum required: ${minContrast}:1`
    );
  }
};

/**
 * CSS for high contrast and large text modes
 */
export const accessibilityStyles = `
  .high-contrast {
    --color-text: #000000;
    --color-background: #FFFFFF;
    --color-primary: #0000EE;
    --color-secondary: #000080;
    --color-accent: #FFFF00;
  }
  
  .high-contrast button {
    border: 2px solid currentColor !important;
  }
  
  .high-contrast a {
    text-decoration: underline !important;
  }
  
  .large-text {
    font-size: 125% !important;
    line-height: 1.6 !important;
  }
  
  .large-text button,
  .large-text input {
    min-height: 48px !important;
    padding: 12px 20px !important;
  }
`;

export default ThemeContext;